import { Component, Input, OnInit } from '@angular/core';
import { PopoverController } from '@ionic/angular';

import { TranslateService } from '@ngx-translate/core';
import { filter, groupBy, includes, map, orderBy, toLower } from 'lodash';

interface TreatmentChoice {
  id: string;
  label: string;
  checked: boolean;
}

export interface MeasureChoice {
  id: string;
  label: string;
  treatments: TreatmentChoice[];
  single: boolean;
  expanded: boolean;
  summary: string;
}

/**
 * CVX-6554: pick what a summary report includes by name, then how each is treated.
 * Options are the report's flat column options; those tagged with `measure` (see
 * models/types/measures.ts) are grouped under one row, the rest are rows of their own.
 * Dismisses with the chosen column ids.
 */
@Component({
  selector: 'app-measure-picker-modal',
  templateUrl: './measure-picker-modal.component.html',
  styleUrls: ['./measure-picker-modal.component.scss'],
})
export class MeasurePickerModalComponent implements OnInit {

  @Input() options: any[] = [];
  @Input() selected: string[] = [];

  public title: string;
  public subtitle: string;
  public measures: MeasureChoice[] = [];
  public visibleMeasures: MeasureChoice[] = [];
  public search = '';
  public hasSelection = false;

  constructor(
    private popoverController: PopoverController,
    private translate: TranslateService
  ) {
  }

  ngOnInit() {
    this.title = this.translate.instant('SHARED.Select_Data');
    this.subtitle = this.translate.instant('REPORTING.Choose_Treatment');

    const groups = groupBy(this.options, (option) => option.measure || option.id);
    this.measures = orderBy(map(groups, (group: any[], id: string) => {
      const single = group.length === 1;
      const measure: MeasureChoice = {
        id,
        label: single && !group[0].measure ? group[0].description : group[0].measureLabel,
        treatments: map(group, (option) => ({
          id: option.id,
          label: option.treatmentLabel || option.description,
          checked: includes(this.selected, option.id)
        })),
        single,
        expanded: false,
        summary: ''
      };
      this.summarize(measure);
      return measure;
    }), (measure) => toLower(measure.label));
    this.hasSelection = this.getSelectedIds().length > 0;
    this.filterMeasures();
  }

  public filterMeasures(): void {
    const search = toLower(this.search).trim();
    this.visibleMeasures = search
      ? filter(this.measures, (measure) => includes(toLower(measure.label), search))
      : this.measures;
  }

  public onSearch(event: any): void {
    this.search = event?.target?.value || '';
    this.filterMeasures();
  }

  /** a single-treatment measure toggles; one with several opens its treatments */
  public onMeasureClick(measure: MeasureChoice): void {
    if (measure.single) {
      this.toggle(measure, measure.treatments[0]);
    } else {
      measure.expanded = !measure.expanded;
    }
  }

  public toggle(measure: MeasureChoice, treatment: TreatmentChoice): void {
    treatment.checked = !treatment.checked;
    this.summarize(measure);
    this.hasSelection = this.getSelectedIds().length > 0;
  }

  public close(): void {
    this.popoverController.dismiss();
  }

  public apply(): void {
    this.popoverController.dismiss(this.getSelectedIds());
  }

  /** previously saved ids keep their order (unknown ones too - never drop a saved column); new ones follow */
  public getSelectedIds(): string[] {
    const checked = map(filter(this.measures.flatMap((measure) => measure.treatments), 'checked'), 'id');
    const known = new Set(map(this.measures.flatMap((measure) => measure.treatments), 'id'));
    const kept = filter(this.selected, (id) => !known.has(id) || includes(checked, id));
    return [...kept, ...filter(checked, (id) => !includes(kept, id))];
  }

  private summarize(measure: MeasureChoice): void {
    const checked = filter(measure.treatments, 'checked');
    measure.summary = measure.single ? '' : map(checked, 'label').join(', ');
  }
}
